import { createId } from '@paralleldrive/cuid2';
import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { TextField } from 'components/Field/TextField';
import { GapSmall } from 'components/GapSmall';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { Txt } from 'components/Txt';
import { useAlert } from 'hooks/useAlert';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { pluralise } from 'lib/pluralise';
import { randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { cloneDeep, difference, isEqual, random } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doFieldSettingsFieldDelete,
  doFieldSettingsFieldSave,
  doFieldSettingsSetFieldId,
  doFieldSettingsFieldSaved,
} from 'store/actions';
import {
  eventReorderScreenDone,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SET_GENERIC,
} from 'store/app/actions';
import { selectGeneric } from 'store/app/selectors';
import { selectUserIsSupport } from 'store/auth/selectors';
import {
  selectFieldSettingsFieldId,
  selectFieldSettingsGroupId,
  selectFieldSettingsLiteGroupId,
  selectOrganisationFieldSettings,
} from 'store/remote/selectors';
import { FieldSettingsField, FieldSettingsTypes } from 'store/remote/types';
import { FieldTypeKey } from 'types/types';

type State = {
  field: FieldSettingsField;
  deleteOptionIds: string[];
  suggestions: Record<string, string>;
};

const makeSuggestionName = () => `suggestion-${random(1, 999999)}`;

export const FieldSettingsFieldModal: React.FC<
  ModalScreenProps<ModalScreens.FieldSettingsField>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const alert = useAlert();
  const forceUpdate = useForceUpdate();

  const isSupport = useSelector(selectUserIsSupport);
  // const organisationId = useSelector(selectRemoteOrganisationId)!;
  const fieldSettings = useSelector(selectOrganisationFieldSettings);
  // const resultsCount = useSelector(selectOrganisationFieldSettingsResultCount);

  const rawLiteGroupId = useSelector(selectFieldSettingsLiteGroupId);
  const rawGroupId = useSelector(selectFieldSettingsGroupId)!;
  // const isLite = !!rawLiteGroupId;
  const groupId = rawLiteGroupId || rawGroupId;

  const fieldId = useSelector(selectFieldSettingsFieldId);
  const { newData } = route.params;

  const incomingCriteriaKey = 'fieldSettingsIncomingCriteria';
  const incomingCriteria = useSelector((state) =>
    selectGeneric(state, incomingCriteriaKey),
  ) as FieldSettingsField['criteria'] | undefined;

  const field = React.useMemo<FieldSettingsField>(
    () =>
      (fieldId ? fieldSettings.fields[fieldId] : undefined) || {
        countResults: 0,
        countResultsByOrganisation: {},
        id: '',
        childGroupId: null,
        multiSelect: !!newData?.multiSelect,
        name: '',
        options: [],
        criteria: [],
        type: newData?.type || FieldTypeKey.note,
        suggestions: [],
        identifier: '',
        identifierEngage: '',
        isLocation: false,
      },
    [fieldSettings.fields, newData?.multiSelect, newData?.type, fieldId],
  );
  const section = Object.values(fieldSettings.sections).find((s) =>
    s.groupIds.includes(rawGroupId),
  )!;
  const childGroup = field.childGroupId
    ? fieldSettings.groups[field.childGroupId]
    : undefined;
  const editable = fieldSettings.editable && !section.managedByOrganisation;

  const afterSaveRef = React.useRef<
    ((savedFieldId: string) => void) | undefined
  >(undefined);
  const dataRef = React.useRef(cloneDeep(field));
  const deleteOptionIdsRef = React.useRef<string[]>([]);
  const dataSuggestionsRef = React.useRef<Record<string, string>>(
    Object.fromEntries(field.suggestions.map((s) => [makeSuggestionName(), s])),
  );

  const [resetDataI, setResetDataI] = React.useState(0);
  const resetDataIRef = React.useRef(resetDataI);
  if (resetDataIRef.current !== resetDataI) {
    dataRef.current = cloneDeep(field);
    deleteOptionIdsRef.current = [];
    dataSuggestionsRef.current = Object.fromEntries(
      field.suggestions.map((s) => [makeSuggestionName(), s]),
    );
    resetDataIRef.current = resetDataI;
  }

  const checkIsDirty = React.useCallback(
    () =>
      !isEqual(field, dataRef.current) ||
      !!deleteOptionIdsRef.current.length ||
      !!difference(field.suggestions, Object.values(dataSuggestionsRef.current))
        .length ||
      !!difference(Object.values(dataSuggestionsRef.current), field.suggestions)
        .length,
    [field],
  );
  const isDirty = React.useRef(false);
  isDirty.current = checkIsDirty();
  const isDirtyVar = isDirty.current;

  const handleDirtyUpdate = React.useCallback(() => {
    if (isDirty.current !== checkIsDirty()) {
      forceUpdate();
    }
  }, [checkIsDirty, forceUpdate]);

  // const [showOrganisationUsage, setShowOrganisationUsage] = React.useState(
  //   section.usedByOrganisations.length < 25,
  // );

  const type = FieldSettingsTypes[dataRef.current.type];

  /** EDIT */

  const canSave = React.useCallback(
    (): boolean => !(!fieldId && !dataRef.current.name),
    [fieldId],
  );
  const showCannotSaveAlert = React.useCallback((): void => {
    alert({ text: 'Please enter a field name' });
  }, [alert]);

  const handleChange = React.useCallback(
    <T extends keyof State['field']>(name: T, value: State['field'][T]) => {
      dataRef.current[name] = value;
      handleDirtyUpdate();
    },
    [handleDirtyUpdate],
  );

  const handleChangeWithUpdate = React.useCallback(
    <T extends keyof State['field']>(name: T, value: State['field'][T]) => {
      dataRef.current[name] = value;
      forceUpdate();
    },
    [forceUpdate],
  );

  const handleSave = React.useCallback(
    (afterSave: (savedFieldId: string) => void) => {
      if (!canSave()) {
        showCannotSaveAlert();
        return;
      }
      if (!fieldId || isDirty.current) {
        afterSaveRef.current = afterSave;
        dispatch(
          fieldId
            ? doFieldSettingsFieldSave({
                id: fieldId,
                data: {
                  name: dataRef.current.name,
                  options: dataRef.current.options,
                  criteria: dataRef.current.criteria,
                  identifier: dataRef.current.identifier,
                  identifierEngage: dataRef.current.identifierEngage,
                  suggestions: Object.values(dataSuggestionsRef.current),
                  deleteOptionIds: deleteOptionIdsRef.current,
                  isLocation: dataRef.current.isLocation,
                },
              })
            : doFieldSettingsFieldSave({
                id: undefined,
                data: {
                  groupId,
                  name: dataRef.current.name,
                  type: dataRef.current.type,
                  multiSelect: dataRef.current.multiSelect,
                  options: dataRef.current.options,
                  criteria: dataRef.current.criteria,
                  identifier: dataRef.current.identifier,
                  identifierEngage: dataRef.current.identifierEngage,
                  suggestions: Object.values(dataSuggestionsRef.current),
                  isLocation: dataRef.current.isLocation,
                },
              }),
        );
        return;
      }
      afterSave(fieldId);
    },
    [canSave, dispatch, fieldId, groupId, showCannotSaveAlert],
  );

  const handleDelete = React.useCallback(() => {
    confirm({
      destructive: true,
      confirmText: 'Delete',
      title: 'Delete Field',
      text: `All results for this field will be deleted${pluralise(
        section.usedByOrganisations.length,
        '\n\nAnother organisation shares this field; continuing will delete it for them as well',
        '\n\n{x} other organisations share this field; continuing will delete it for them as well',
        '',
      )}\n\nThis cannot be undone`,
      onConfirm: () =>
        dispatch(
          doFieldSettingsFieldDelete({
            id: fieldId!,
          }),
        ),
    });
  }, [confirm, dispatch, fieldId, section.usedByOrganisations.length]);

  const handleChangeOptionText = React.useCallback(
    (optionId: string, name: string) => {
      handleChange(
        'options',
        dataRef.current.options.map((option) =>
          option.id === optionId ? { ...option, name } : option,
        ),
      );
      forceUpdate();
    },
    [forceUpdate, handleChange],
  );

  const handleAddOption = () => {
    dataRef.current.options.push({ id: createId(), name: '', countResults: 0 });
    forceUpdate();
  };

  const handleRemoveOption = React.useCallback(
    (optionId: string) => () => {
      deleteOptionIdsRef.current = [...deleteOptionIdsRef.current, optionId];
      dataRef.current.options = dataRef.current.options.filter(
        (o) => o.id !== optionId,
      );
      forceUpdate();
    },
    [forceUpdate],
  );

  const handleAddSuggestion = () => {
    dataSuggestionsRef.current = {
      ...dataSuggestionsRef.current,
      [makeSuggestionName()]: '',
    };
    forceUpdate();
  };

  const handleRemoveSuggestion = (suggestionKey: string) => {
    delete dataSuggestionsRef.current[suggestionKey];
    forceUpdate();
  };

  const handleChangeSuggestion = (suggestionKey: string, value: string) => {
    dataSuggestionsRef.current[suggestionKey] = value;
    handleDirtyUpdate();
  };

  const handleNavigateToCriteria = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.FieldSettingsFieldCriteria,
        params: {
          groupId,
          fieldId: dataRef.current.id,
          fieldName: dataRef.current.name,
          criteria: dataRef.current.criteria,
          selectionKey: incomingCriteriaKey,
        },
      }),
    );

  // const usageByOrganisation = React.useMemo<
  //   Array<{
  //     countResults: number;
  //     id: string;
  //     name: string;
  //   }>
  // >(
  //   () =>
  //     [
  //       { id: organisationId, name: 'This Organisation' },
  //       ...orderBy(section.usedByOrganisations, 'name'),
  //     ].map((usedByOrganisation) => ({
  //       id: usedByOrganisation.id,
  //       name: usedByOrganisation.name,
  //       countResults: fieldId
  //         ? resultsCount?.[fieldId]?.[usedByOrganisation.id] || 0
  //         : 0,
  //     })),
  //   [organisationId, section.usedByOrganisations, resultsCount, fieldId],
  // );

  React.useEffect(() => {
    if (incomingCriteria) {
      dataRef.current.criteria = incomingCriteria;
      dispatch(SET_GENERIC({ key: incomingCriteriaKey }));
    }
  }, [dispatch, incomingCriteria]);

  // Navigate back when id given but no state.field.id; the field was deleted
  React.useEffect(() => {
    if (fieldId && !field.id) {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  }, [dispatch, fieldId, field.id]);

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave(() => dispatch(NAVIGATE_MODAL_BACK())),
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, handleSave, dispatch]);

  const reorderEventKey = React.useRef(randomKey());

  React.useEffect(() => {
    const events = [
      doAddEvent(doFieldSettingsFieldSaved, (action) => {
        afterSaveRef.current?.(action.payload);
        afterSaveRef.current = undefined;
        setResetDataI((i) => i + 1);
      }),
      doAddEvent(eventReorderScreenDone, (action) => {
        if (action.payload.key === reorderEventKey.current) {
          // dataRef.current.options = [...data];
          dataRef.current.options = action.payload.stringValues.reduce<
            typeof dataRef.current.options
          >((acc, optionId) => {
            const option = dataRef.current.options.find(
              (o) => o.id === optionId,
            );
            if (option) {
              acc.push(option);
            }
            return acc;
          }, []);
          forceUpdate();
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
      dispatch(doFieldSettingsSetFieldId(undefined));
    };
  }, [dispatch, forceUpdate]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={
            fieldId
              ? `${
                  dataRef.current.multiSelect
                    ? type.labelMultiSelect || type.label || ''
                    : type.label || ''
                } Field`
              : `Add ${
                  dataRef.current.multiSelect
                    ? type.labelMultiSelect || type.label || ''
                    : type.label || ''
                } Field`
          }
          onPressCancelButton={handleClose}
          onPressSaveButton={
            editable
              ? () => handleSave(() => dispatch(NAVIGATE_MODAL_BACK()))
              : undefined
          }
        />
      }
    >
      <Card
        label="Field Name"
        explanation={
          section.managedByOrganisation
            ? `This field is managed by ${section.managedByOrganisation.name} and cannot be modified`
            : ''
        }
      >
        {dataRef.current.type === FieldTypeKey.group ? (
          <CardRow
            label={childGroup?.name || 'No Name'}
            label2="Repeatable Group"
            icon={ICONS.LayerGroupLight}
          />
        ) : editable ? (
          <TextField
            value={dataRef.current.name}
            name="name"
            autoFocus={!fieldId}
            placeholder={field.name || 'Enter a name for this field'}
            onChangeWithName={handleChange}
            autoCapitalize="words"
            onEsc={handleClose}
            autoFocusWeb
            autoFocusNative={!fieldId}
            autoFocusDelay
          />
        ) : (
          <CardRow label={dataRef.current.name} />
        )}
      </Card>

      {isSupport && (
        <Card label="Identifier">
          {editable ? (
            <TextField
              value={dataRef.current.identifier}
              name="identifier"
              placeholder="No Identifier"
              onChangeWithName={handleChange}
              onEsc={handleClose}
              rbs
              textLeft="Normal"
              textLeftStyle={{ width: 55 }}
              autoCorrect={false}
              autoComplete="off"
              autoCapitalize="none"
            />
          ) : (
            <CardRow
              label={dataRef.current.identifier}
              placeholder="No Identifier"
              rbs
            />
          )}
          {editable ? (
            <TextField
              value={dataRef.current.identifierEngage}
              name="identifierEngage"
              placeholder="No Engage Identifier"
              onChangeWithName={handleChange}
              onEsc={handleClose}
              rbs
              textLeft="Engage"
              textLeftStyle={{ width: 55 }}
              autoCorrect={false}
              autoComplete="off"
              autoCapitalize="none"
            />
          ) : (
            <CardRow
              label={dataRef.current.identifierEngage}
              placeholder="No Engage Identifier"
              rbs
            />
          )}
        </Card>
      )}

      {[FieldSettingsTypes.file.id, FieldSettingsTypes.image.id].includes(
        type.id,
      ) &&
        type.allowMultiSelect && (
          <Card explanation="Multi select cannot be changed after the field is created">
            <CardRow
              label="Multi Select"
              checkedValue={dataRef.current.multiSelect}
              onPress={
                !fieldId
                  ? () =>
                      handleChangeWithUpdate(
                        'multiSelect',
                        !dataRef.current.multiSelect,
                      )
                  : undefined
              }
            />
          </Card>
        )}

      {dataRef.current.type === FieldTypeKey.options && (
        <View>
          <Card
            label="Options"
            labelLinkText="Reorder"
            onLabelLinkPress={
              dataRef.current.options.length > 1
                ? () =>
                    dispatch(
                      NAVIGATE_MODAL({
                        screen: ModalScreens.REORDER,
                        params: {
                          title: 'Reorder Options',
                          options: dataRef.current.options.reduce<
                            { value: string; name: string }[]
                          >((acc, option) => {
                            acc.push({
                              value: option.id,
                              name: option.name,
                            });
                            return acc;
                          }, []),
                          eventKey: reorderEventKey.current,
                        },
                      }),
                    )
                : undefined
            }
          ></Card>
          {editable ? (
            <GapSmall>
              {!!dataRef.current.options.length && (
                <Card>
                  {dataRef.current.options.map((option) => (
                    <TextField
                      key={option.id}
                      onDelete={handleRemoveOption(option.id)}
                      name={option.id}
                      onChangeWithName={handleChangeOptionText}
                      value={option.name}
                      onEsc={handleClose}
                      autoFocusWeb={!option.name}
                    />
                  ))}
                </Card>
              )}
              <Card
                explanation={
                  fieldId
                    ? 'Removing an option will also delete results using the option'
                    : undefined
                }
              >
                <CardRow
                  label="Add Option"
                  labelColor={theme.colors.blue}
                  onPress={handleAddOption}
                  onAdd={handleAddOption}
                />
              </Card>
            </GapSmall>
          ) : (
            <Card label="Options">
              {dataRef.current.options.length ? (
                dataRef.current.options.map((option) => (
                  <CardRow key={option.id} label={option.name} />
                ))
              ) : (
                <CardRow value="This field has no options" />
              )}
            </Card>
          )}
        </View>
      )}

      {(dataRef.current.type === FieldTypeKey.text ||
        dataRef.current.type === FieldTypeKey.note) &&
        (editable ? (
          <View>
            <Card label="Suggestions"></Card>
            <GapSmall>
              {!!Object.keys(dataSuggestionsRef.current).length && (
                <Card>
                  {Object.entries(dataSuggestionsRef.current).map(
                    ([suggestionKey, suggestion]) =>
                      dataRef.current.type === FieldTypeKey.text ? (
                        <TextField
                          key={suggestionKey}
                          placeholder="Enter suggestion..."
                          value={suggestion}
                          onDelete={() => handleRemoveSuggestion(suggestionKey)}
                          name={suggestionKey}
                          onChangeWithName={handleChangeSuggestion}
                          onEsc={handleClose}
                          autoFocusWeb={!suggestion}
                        />
                      ) : (
                        <TextAreaField
                          key={suggestionKey}
                          placeholder="Enter suggestion..."
                          value={suggestion}
                          onDelete={() => handleRemoveSuggestion(suggestionKey)}
                          name={suggestionKey}
                          onChangeWithName={handleChangeSuggestion}
                          onEsc={handleClose}
                          autoFocusWeb={!suggestion}
                        />
                      ),
                  )}
                </Card>
              )}
              <Card explanation="Suggestions are shown when completing the field">
                <CardRow
                  label="Add Suggestion"
                  labelColor={theme.colors.blue}
                  onPress={handleAddSuggestion}
                  onAdd={handleAddSuggestion}
                />
              </Card>
            </GapSmall>
          </View>
        ) : (
          <Card
            label="Suggestions"
            explanation="Suggestions are shown when completing the field"
          >
            {Object.keys(dataSuggestionsRef.current).length ? (
              Object.entries(dataSuggestionsRef.current).map(
                ([suggestionKey, suggestion]) => (
                  <CardRow
                    key={suggestionKey}
                    label={suggestion}
                    labelNumberOfLines={0}
                  />
                ),
              )
            ) : (
              <CardRow label="No Suggestions" />
            )}
          </Card>
        ))}

      {![
        FieldTypeKey.image,
        FieldTypeKey.file,
        FieldTypeKey.pin,
        FieldTypeKey.qr,
        FieldTypeKey.group,
        FieldTypeKey.divider,
      ].includes(dataRef.current.type) && (
        <Card explanation="Field identifies the location of this element (e.g. floor, room, etc)">
          <CardRow
            label="Identifies Location"
            checkedValue={dataRef.current.isLocation}
            onPress={
              editable
                ? () =>
                    handleChangeWithUpdate(
                      'isLocation',
                      !dataRef.current.isLocation,
                    )
                : undefined
            }
          />
        </Card>
      )}

      {dataRef.current.type !== FieldTypeKey.group &&
        dataRef.current.type !== FieldTypeKey.divider && (
          <Card label="Show if...">
            {dataRef.current.criteria.length ? (
              <CardRow
                onPress={editable ? handleNavigateToCriteria : undefined}
              >
                {dataRef.current.criteria.map<React.ReactNode>(
                  (criterion, index) => {
                    const criterionField =
                      fieldSettings.fields[criterion.fieldId]!;
                    return (
                      <React.Fragment key={criterion.fieldId}>
                        {index > 0 && (
                          <Txt
                            weight="bold"
                            size="xsmall"
                            style={{ color: '#999' }}
                          >
                            AND
                          </Txt>
                        )}
                        <Txt>
                          <Txt weight="medium">{criterionField.name}</Txt>{' '}
                          includes{' '}
                          {criterion.value.map(
                            (criteriaOptionId, coIndex) =>
                              `${coIndex > 0 ? ' or ' : ''}` +
                              '‘' +
                              `${
                                criterionField.options.find(
                                  (o) => o.id === criteriaOptionId,
                                )?.name
                              }` +
                              '’',
                          )}
                        </Txt>
                      </React.Fragment>
                    );
                  },
                )}
              </CardRow>
            ) : (
              <CardRow
                label="Always Show"
                value={editable ? 'Change...' : undefined}
                onPress={editable ? handleNavigateToCriteria : undefined}
              />
            )}
          </Card>
        )}

      {!!fieldId && editable && (
        <Button
          text="Delete Field"
          destructive
          style={{ alignSelf: 'center' }}
          onPress={handleDelete}
        />
      )}
    </Page>
  );
};
